import type { AudioEngine } from '../audio/engine';
import type { TimingIntent } from '../state/timing';
import type { Recording, SessionPlan } from '../types';

export interface StrictPlanOptions {
  toleranceSec: 0;
}

export type StartPlanner = (
  recording: Recording,
  targetSec: number,
  options: StrictPlanOptions,
) => SessionPlan;

export type PlanResolution =
  | { kind: 'duration'; targetSec: number }
  | {
    kind: 'deadline';
    targetSec: number;
    deadlineMs: number;
    launchSlackMs: number;
    attempt: number;
  };

export interface SessionStartOptions {
  now?: () => number;
  /**
   * Expected delay between requesting play and the renderer beginning. Keep this based on
   * a device measurement: the controller can reserve the time but cannot observe it.
   */
  launchSlackMs?: number;
  /** Number of revised deadline plans that may be loaded after the asset-warming load. */
  maxDeadlineLoads?: number;
  /** Publish the exact plan/trace identity before playback can be requested. */
  onPlanResolved?: (plan: SessionPlan, resolution: PlanResolution) => void | Promise<void>;
  /** An epoch guard supplied by the owner when another start may supersede this one. */
  isValid?: () => boolean;
}

export interface PreparedSessionStart {
  plan: SessionPlan;
  startupDelayMs: number;
  /** Wall-clock sample immediately before `engine.play()` was invoked. */
  playbackRequestedAtMs: number;
}

export class DeadlineStartError extends Error {
  readonly code: 'deadline_expired' | 'deadline_did_not_converge';
  constructor(
    code: 'deadline_expired' | 'deadline_did_not_converge',
    message: string,
  ) {
    super(message);
    this.code = code;
    this.name = 'DeadlineStartError';
  }
}

export class StaleSessionStartError extends Error {
  constructor() {
    super('This session start was superseded.');
    this.name = 'StaleSessionStartError';
  }
}

/**
 * Prepare and start one pinned recording without letting asset work weaken a deadline.
 * Planning remains outside this module so the caller supplies its cadence/edit policy.
 */
export async function prepareSessionStart(
  engine: Pick<AudioEngine, 'load' | 'play'>,
  recording: Recording,
  initialPlan: SessionPlan,
  timingIntent: TimingIntent,
  planner: StartPlanner,
  options: SessionStartOptions = {},
): Promise<PreparedSessionStart> {
  const now = options.now ?? Date.now;
  const startedAtMs = now();
  const isValid = options.isValid ?? (() => true);
  const assertValid = () => {
    if (!isValid()) throw new StaleSessionStartError();
  };

  assertValid();
  await engine.load(recording, initialPlan);
  assertValid();

  if (timingIntent.kind === 'duration') {
    await options.onPlanResolved?.(initialPlan, {
      kind: 'duration',
      targetSec: timingIntent.durationSec,
    });
    assertValid();
    const playbackRequestedAtMs = now();
    await engine.play();
    assertValid();
    return {
      plan: initialPlan,
      startupDelayMs: playbackRequestedAtMs - startedAtMs,
      playbackRequestedAtMs,
    };
  }

  const launchSlackMs = Math.max(0, options.launchSlackMs ?? 0);
  const maxLoads = Math.max(1, Math.floor(options.maxDeadlineLoads ?? 2));

  for (let attempt = 1; attempt <= maxLoads; attempt += 1) {
    assertValid();
    const targetSec = deadlineTargetSec(timingIntent.deadlineMs, now(), launchSlackMs);
    if (targetSec <= 0) throw expiredDeadline();

    const resolved = planner(recording, targetSec, { toleranceSec: 0 });
    await options.onPlanResolved?.(resolved, {
      kind: 'deadline',
      targetSec,
      deadlineMs: timingIntent.deadlineMs,
      launchSlackMs,
      attempt,
    });
    assertValid();

    // Hashing or UI/trace publication may itself consume a whole target second.
    if (!fitsDeadline(resolved, timingIntent.deadlineMs, now(), launchSlackMs)) continue;

    await engine.load(recording, resolved);
    assertValid();
    const playbackRequestedAtMs = now();
    if (!fitsDeadline(resolved, timingIntent.deadlineMs, playbackRequestedAtMs, launchSlackMs)) {
      continue;
    }

    await engine.play();
    assertValid();
    return {
      plan: resolved,
      startupDelayMs: playbackRequestedAtMs - startedAtMs,
      playbackRequestedAtMs,
    };
  }

  if (timingIntent.deadlineMs <= now() + launchSlackMs) throw expiredDeadline();
  throw new DeadlineStartError(
    'deadline_did_not_converge',
    'Session preparation remained too slow to promise the selected finish time.',
  );
}

function deadlineTargetSec(deadlineMs: number, nowMs: number, launchSlackMs: number): number {
  return Math.floor((deadlineMs - nowMs - launchSlackMs) / 1000);
}

function fitsDeadline(
  plan: SessionPlan,
  deadlineMs: number,
  nowMs: number,
  launchSlackMs: number,
): boolean {
  return plan.actualSec * 1000 <= deadlineMs - nowMs - launchSlackMs;
}

function expiredDeadline(): DeadlineStartError {
  return new DeadlineStartError('deadline_expired', 'The selected finish time has passed.');
}
